import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowDown, ArrowLeft, ArrowUp, BookOpen, ExternalLink, Plus } from "lucide-react";
import { useAuth } from "../../services/AuthContext";
import {
  createAdminLesson,
  getAdminCourse,
  reorderAdminLessons,
  updateAdminCourse,
} from "../../services/admin";
import AdminReloadOverlay from "./AdminReloadOverlay";

const emptyForm = {
  title: "",
  instructor: "",
  description: "",
  requirements: "",
  availability: "paid",
  thumbnail_url: "",
  category: "",
};

function lessonsBySection(course) {
  const map = {};
  (course?.sections || []).forEach((section) => {
    map[section.code] = [...(section.lessons || [])];
  });
  return map;
}

export default function AdminCoursePage() {
  const { courseCode } = useParams();
  const { getIdToken } = useAuth();
  const [course, setCourse] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [sectionLessons, setSectionLessons] = useState({});
  const [dirtySections, setDirtySections] = useState({});
  const [newLessonTitle, setNewLessonTitle] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingOrder, setSavingOrder] = useState({});
  const [creatingLesson, setCreatingLesson] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const applyCourse = (data) => {
    setCourse(data);
    setSectionLessons(lessonsBySection(data));
    setDirtySections({});
    setForm({
      title: data.title || "",
      instructor: data.instructor || "",
      description: data.description || "",
      requirements: data.requirements || "",
      availability: data.availability || "paid",
      thumbnail_url: data.thumbnail_url || "",
      category: data.category || "",
    });
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const token = await getIdToken();
        const data = await getAdminCourse(token, courseCode);
        if (cancelled) return;
        applyCourse(data);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Não foi possível carregar o curso.");
          setCourse(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [courseCode, getIdToken]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError("");
      setSuccess("");
      const token = await getIdToken();
      const updated = await updateAdminCourse(token, courseCode, form);
      applyCourse(updated);
      setSuccess("Curso atualizado com sucesso.");
    } catch (err) {
      setError(err.message || "Não foi possível salvar o curso.");
    } finally {
      setSaving(false);
    }
  };

  const moveLesson = (sectionCode, index, direction) => {
    setSectionLessons((prev) => {
      const current = [...(prev[sectionCode] || [])];
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.length) return prev;
      const swapped = current[index];
      current[index] = current[nextIndex];
      current[nextIndex] = swapped;
      return { ...prev, [sectionCode]: current };
    });
    setDirtySections((prev) => ({ ...prev, [sectionCode]: true }));
  };

  const saveLessonOrder = async (sectionCode) => {
    try {
      setSavingOrder((prev) => ({ ...prev, [sectionCode]: true }));
      setError("");
      setSuccess("");
      const token = await getIdToken();
      const lessonCodes = (sectionLessons[sectionCode] || []).map((lesson) => lesson.code);
      const updated = await reorderAdminLessons(token, courseCode, sectionCode, lessonCodes);
      applyCourse(updated);
      setSuccess("Ordem das aulas salva com sucesso.");
    } catch (err) {
      setError(err.message || "Não foi possível salvar a ordem das aulas.");
    } finally {
      setSavingOrder((prev) => ({ ...prev, [sectionCode]: false }));
    }
  };

  const handleCreateLesson = async (sectionCode) => {
    const title = (newLessonTitle[sectionCode] || "").trim();
    if (!title) {
      setError("Digite o título da nova aula.");
      return;
    }
    try {
      setCreatingLesson((prev) => ({ ...prev, [sectionCode]: true }));
      setError("");
      setSuccess("");
      const token = await getIdToken();
      const updated = await createAdminLesson(token, courseCode, sectionCode, { title });
      applyCourse(updated);
      setNewLessonTitle((prev) => ({ ...prev, [sectionCode]: "" }));
      setSuccess("Aula criada com sucesso.");
    } catch (err) {
      setError(err.message || "Não foi possível criar a aula.");
    } finally {
      setCreatingLesson((prev) => ({ ...prev, [sectionCode]: false }));
    }
  };

  if (!course) {
    return (
      <div className="admin-page">
        <AdminReloadOverlay visible={loading} message="Atualizando curso..." />
        {!loading && (
          <>
            <Link to="/admin/content" className="admin-back">
              <ArrowLeft size={16} /> Voltar aos cursos
            </Link>
            <div className="admin-error">{error || "Curso não encontrado."}</div>
          </>
        )}
      </div>
    );
  }

  const sections = course.sections || [];

  return (
    <div className="admin-page">
      <AdminReloadOverlay
        visible={
          loading ||
          saving ||
          Object.values(savingOrder).some(Boolean) ||
          Object.values(creatingLesson).some(Boolean)
        }
        message="Atualizando curso..."
      />
      <Link to="/admin/content" className="admin-back">
        <ArrowLeft size={16} /> Voltar aos cursos
      </Link>
      <div className="admin-page-header">
        <h1 className="admin-title">
          <BookOpen size={30} className="admin-title-icon" />
          {course.title || course.code}
        </h1>
        <p className="admin-subtitle">Edite os dados do curso, reordene aulas e crie novas aulas.</p>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {success && <div className="admin-success">{success}</div>}

      <div className="admin-panel">
        <h2 className="admin-panel-title">Dados do curso</h2>
        <form onSubmit={handleSave}>
          <div className="admin-form-grid">
            <div className="admin-control">
              <label className="admin-control-label" htmlFor="course-title">Título</label>
              <input id="course-title" className="admin-control-input" value={form.title} onChange={handleChange("title")} />
            </div>
            <div className="admin-control">
              <label className="admin-control-label" htmlFor="course-instructor">Professor</label>
              <input id="course-instructor" className="admin-control-input" value={form.instructor} onChange={handleChange("instructor")} />
            </div>
            <div className="admin-control">
              <label className="admin-control-label" htmlFor="course-category">Categoria</label>
              <input id="course-category" className="admin-control-input" value={form.category} onChange={handleChange("category")} />
            </div>
            <div className="admin-control">
              <label className="admin-control-label" htmlFor="course-availability">Disponibilidade</label>
              <select
                id="course-availability"
                className="admin-control-select"
                value={form.availability}
                onChange={handleChange("availability")}
              >
                <option value="free">Gratuito</option>
                <option value="paid">Premium</option>
              </select>
            </div>
            <div className="admin-control admin-span-2">
              <label className="admin-control-label" htmlFor="course-thumb">URL da capa</label>
              <input id="course-thumb" className="admin-control-input" value={form.thumbnail_url} onChange={handleChange("thumbnail_url")} />
            </div>
            <div className="admin-control admin-span-2">
              <label className="admin-control-label" htmlFor="course-description">Descrição</label>
              <textarea id="course-description" className="admin-control-textarea" value={form.description} onChange={handleChange("description")} />
            </div>
            <div className="admin-control admin-span-2">
              <label className="admin-control-label" htmlFor="course-requirements">Requisitos</label>
              <textarea id="course-requirements" className="admin-control-textarea" value={form.requirements} onChange={handleChange("requirements")} />
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <button type="submit" className="admin-primary-btn" disabled={saving}>
              {saving ? "Salvando..." : "Salvar curso"}
            </button>
          </div>
        </form>
      </div>

      <div className="admin-panel">
        <h2 className="admin-panel-title">Seções e aulas</h2>
        {sections.length === 0 && <div className="admin-empty">Este curso ainda não tem seções.</div>}
        {sections.map((section) => {
          const lessons = sectionLessons[section.code] || [];
          return (
            <div key={section.code || section.id} className="admin-section-block">
              <h3 className="admin-section-title">{section.title || section.code}</h3>
              {section.description && <p className="admin-muted">{section.description}</p>}
              <ul className="admin-lesson-list">
                {lessons.map((lesson, index) => (
                  <li key={lesson.code || lesson.id} className="admin-lesson-row">
                    <div className="admin-lesson-order">
                      <button
                        type="button"
                        className="admin-icon-btn"
                        disabled={index === 0}
                        onClick={() => moveLesson(section.code, index, -1)}
                        aria-label="Mover aula para cima"
                      >
                        <ArrowUp size={16} />
                      </button>
                      <button
                        type="button"
                        className="admin-icon-btn"
                        disabled={index === lessons.length - 1}
                        onClick={() => moveLesson(section.code, index, 1)}
                        aria-label="Mover aula para baixo"
                      >
                        <ArrowDown size={16} />
                      </button>
                    </div>
                    <Link
                      className="admin-lesson-edit-link"
                      to={`/admin/content/${courseCode}/lessons/${section.code}/${lesson.code}`}
                    >
                      <span>{lesson.title || lesson.code}</span>
                      <span className="admin-muted">{lesson.code}</span>
                    </Link>
                    <a
                      className="admin-icon-btn admin-lesson-view-btn"
                      href={`/courses/${courseCode}/sections/${section.code}/lessons/${lesson.code}`}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Ver aula"
                      title="Ver aula"
                    >
                      <ExternalLink size={16} />
                    </a>
                  </li>
                ))}
              </ul>
              <div className="admin-lesson-actions">
                <button
                  type="button"
                  className="admin-primary-btn"
                  disabled={!dirtySections[section.code] || savingOrder[section.code]}
                  onClick={() => saveLessonOrder(section.code)}
                >
                  {savingOrder[section.code] ? "Salvando ordem..." : "Salvar ordem das aulas"}
                </button>
              </div>
              <div className="admin-new-lesson">
                <input
                  className="admin-control-input"
                  placeholder="Título da nova aula"
                  value={newLessonTitle[section.code] || ""}
                  onChange={(event) =>
                    setNewLessonTitle((prev) => ({ ...prev, [section.code]: event.target.value }))
                  }
                />
                <button
                  type="button"
                  className="admin-secondary-btn"
                  disabled={creatingLesson[section.code]}
                  onClick={() => handleCreateLesson(section.code)}
                >
                  <Plus size={16} />
                  {creatingLesson[section.code] ? "Criando..." : "Criar aula"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
